import React from 'react';
import { Modal, Button } from 'react-bootstrap';

/**
 * SessionTimeoutWarning Component
 * 
 * Displays a warning modal when the user's session is about to expire.
 * Shows a countdown timer and options to stay logged in or logout.
 * 
 * SECURITY FEATURES:
 * - Modal is non-dismissible (backdrop="static", keyboard={false})
 * - Clear call-to-action for security awareness
 * - Countdown provides urgency
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onStayLoggedIn - Called when user clicks "Stay Logged In"
 * @param {Function} props.onLogout - Called when user clicks "Logout"
 * @param {number} props.secondsRemaining - Seconds until automatic logout
 */
const SessionTimeoutWarning = ({ 
    isOpen, 
    onStayLoggedIn, 
    onLogout, 
    secondsRemaining 
}) => {
    // Format seconds as MM:SS
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <Modal 
            show={isOpen} 
            onHide={onStayLoggedIn}
            backdrop="static" 
            keyboard={false} 
            centered
            size="md"
        >
            <Modal.Header style={{ 
                backgroundColor: '#FFF3CD', 
                borderBottom: '1px solid #FFECB5' 
            }}>
                <Modal.Title style={{ 
                    color: '#856404',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    <span role="img" aria-label="warning">⚠️</span>
                    Session Expiring Soon
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body style={{ padding: '24px' }}>
                <div style={{ textAlign: 'center' }}>
                    <p style={{ 
                        fontSize: '16px', 
                        marginBottom: '20px',
                        color: '#333'
                    }}>
                        Your session will expire due to inactivity.
                    </p>
                    
                    <div style={{
                        fontSize: '48px',
                        fontWeight: 'bold',
                        color: secondsRemaining <= 30 ? '#DC3545' : '#856404',
                        marginBottom: '20px',
                        fontFamily: 'monospace'
                    }}>
                        {formatTime(secondsRemaining)}
                    </div>
                    
                    <p style={{ 
                        fontSize: '14px', 
                        color: '#666',
                        marginBottom: '0'
                    }}>
                        For your security, you will be automatically logged out when the timer reaches zero.
                    </p>
                </div>
            </Modal.Body>
            
            <Modal.Footer style={{ 
                justifyContent: 'center', 
                gap: '12px',
                padding: '16px 24px'
            }}>
                <Button 
                    variant="outline-secondary" 
                    onClick={onLogout}
                    style={{ minWidth: '120px' }}
                >
                    Logout Now
                </Button>
                <Button 
                    variant="success" 
                    onClick={onStayLoggedIn}
                    style={{ minWidth: '150px' }}
                >
                    Stay Logged In
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SessionTimeoutWarning;
